"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";

import { BindableInput } from "./BindableInput";
import { File as SpotFile } from "@/types";
import { FileUpload } from "@/components/Form/FileInputField";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { DataTypeIcon } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";

/**
 * V2 renderer for `display=ConfigBindableTypedDictRows`. A user-named map where
 * each row is a `{type, value}` pair: a name input, a type select, and a
 * bindable value whose editor + picker filter follow the selected type. Stored
 * as `{name: {type, value}}`; the value is a literal, an uploaded SpotFile, or
 * a `:var[...]` ref. The picker affordance + chip semantics come from
 * {@link BindableInput}.
 *
 * The row `type` is one of the editor's flow types (`str`/`number`/`bool`/
 * `datetime`/`File`) — the same set the Input node offers — and doubles as the
 * picker's `dataType`. A `list[T]` type makes the row bind-only (a typed-in
 * literal is meaningless for a list) with the picker filtered to list sources
 * of `T`. It's a FE concern only: the runtime unwraps each row's value and
 * keeps auto-detecting File values.
 */

type TypedRow = { type: string; value: unknown };
type TypeOption = { value: string; label: string };

const FALLBACK_OPTION: TypeOption = { value: "str", label: "Text" };

const LIST_TYPE_PATTERN = /^list\[(.+)\]$/;

function parseRowType(type: string): { elementType: string; isList: boolean } {
  const match = LIST_TYPE_PATTERN.exec(type);
  return match
    ? { elementType: match[1], isList: true }
    : { elementType: type, isList: false };
}

// Type options come from the backend as `ConfigChipsSelect`-style chips
// ({value, label, custom_fields}) or plain strings; normalize to {value,label}.
function normalizeTypeOptions(values: unknown[]): TypeOption[] {
  const opts: TypeOption[] = [];
  for (const v of values) {
    if (typeof v === "string") {
      opts.push({ value: v, label: v });
    } else if (v && typeof v === "object" && "value" in v) {
      const value = String((v as { value: unknown }).value);
      const rawLabel = (v as { label?: unknown }).label;
      opts.push({
        value,
        label: typeof rawLabel === "string" ? rawLabel : value,
      });
    }
  }
  return opts.length > 0 ? opts : [FALLBACK_OPTION];
}

const isWrappedRow = (v: unknown): v is TypedRow =>
  !!v &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  "type" in v &&
  "value" in v;

const isSpotFileLike = (v: unknown): boolean =>
  !!v && typeof v === "object" && !Array.isArray(v) && "uri" in v;

/**
 * Coerce the stored map into typed rows. New rows are already `{type, value}`;
 * legacy `ConfigBindableDictRows` rows are a bare value (string ref, literal,
 * or SpotFile) — wrap them, inferring File for SpotFile-shaped values and the
 * field default otherwise so the editor renders sensibly.
 */
function normalizeRows(
  raw: unknown,
  defaultType: string
): Record<string, TypedRow> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, TypedRow> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isWrappedRow(value)) {
      out[key] = { type: String(value.type), value: value.value };
    } else {
      out[key] = {
        type: isSpotFileLike(value) ? "File" : defaultType,
        value,
      };
    }
  }
  return out;
}

/**
 * One row's value editor, dispatched on the row's `type`. File gets the upload
 * picker as the literal (with the bind-to-variable braces layered on by
 * {@link BindableInput}); every other type is a single bindable input whose
 * literal editor adapts to the type (text/number/bool) and whose picker is
 * filtered to compatible sources via `dataType`.
 */
function RowValueEditor({
  nodeId,
  type,
  value,
  disabled,
  onChange,
}: {
  nodeId: string;
  type: string;
  value: unknown;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const isRef = typeof value === "string" && WHOLE_VAR_REF_PATTERN.test(value);
  const { elementType, isList } = parseRowType(type);

  if (isList) {
    return (
      <BindableInput
        nodeId={nodeId}
        dataType={elementType}
        isList
        variableOnly
        value={isRef ? (value as string) : ""}
        disabled={disabled}
        onChange={(next) => onChange(next)}
      />
    );
  }

  if (type === "File") {
    const values: SpotFile[] =
      !isRef && value && typeof value === "object"
        ? Array.isArray(value)
          ? (value as SpotFile[])
          : "uri" in value
            ? [value as SpotFile]
            : []
        : [];
    return (
      <BindableInput
        nodeId={nodeId}
        dataType="File"
        value={isRef ? (value as string) : ""}
        disabled={disabled}
        renderLiteral={
          <FileUpload
            variant="inline"
            values={values}
            isDisabled={disabled}
            canSelectMultipleFiles={false}
            showSelectedFiles
            setValues={(files) => onChange(files[0] ?? null)}
          />
        }
        onChange={(next) => onChange(next)}
      />
    );
  }

  return (
    <BindableInput
      nodeId={nodeId}
      dataType={type}
      value={typeof value === "string" ? value : ""}
      disabled={disabled}
      placeholder="Value"
      onChange={(next) => onChange(next)}
    />
  );
}

export function BindableTypedDictRowsField({
  nodeId,
  fieldKey,
  values,
  defaultValue,
  addLabel = "Add variable",
  label,
  tooltip,
  description,
  disabled,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  values: unknown[];
  defaultValue: string;
  addLabel?: string;
  label?: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as Record<string, unknown> | undefined;
  const rows = useMemo(
    () => normalizeRows(raw, defaultValue),
    [raw, defaultValue]
  );
  const typeOptions = useMemo(() => normalizeTypeOptions(values), [values]);
  const invalid = !!error;

  const write = (next: Record<string, TypedRow>) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  const setRow = (name: string, patch: Partial<TypedRow>) =>
    write({ ...rows, [name]: { ...rows[name], ...patch } });

  const renameRow = (oldName: string, newName: string) => {
    if (!newName || newName === oldName || rows[newName] !== undefined) return;
    const next: Record<string, TypedRow> = {};
    for (const [k, v] of Object.entries(rows))
      next[k === oldName ? newName : k] = v;
    write(next);
  };

  const removeRow = (name: string) => {
    const next = { ...rows };
    delete next[name];
    write(next);
  };

  const addRow = () => {
    let i = Object.keys(rows).length + 1;
    while (rows[`Input ${i}`] !== undefined) i += 1;
    write({ ...rows, [`Input ${i}`]: { type: defaultValue, value: "" } });
  };

  const entries = Object.entries(rows);

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
          isRequired={isRequired}
          showOptionalLabel={showOptionalLabel}
        />
      ) : null}
      {description ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <div
        className={cn(
          "flex w-full flex-col gap-6",
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70 p-1.5"
        )}
      >
        {entries.map(([key, row]) => (
          <div key={key} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                disabled={disabled}
                defaultValue={key}
                className="h-8 min-w-0 flex-1 bg-background"
                onBlur={(e) => renameRow(key, e.target.value.trim())}
              />
              <Select
                disabled={disabled}
                value={row.type || defaultValue}
                onValueChange={(v) => {
                  // Switching type usually invalidates the current literal or
                  // binding, so reset the value alongside the type.
                  if (v !== row.type) setRow(key, { type: v, value: "" });
                }}
              >
                <SelectTrigger className="h-8 w-36 shrink-0 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((opt) => {
                    const optType = parseRowType(opt.value);
                    return (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <DataTypeIcon
                            dataType={optType.elementType}
                            isList={optType.isList}
                            size={14}
                          />
                          {opt.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove row"
                disabled={disabled}
                className="size-8 shrink-0 text-muted-foreground"
                onClick={() => removeRow(key)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <RowValueEditor
              nodeId={nodeId}
              type={row.type || defaultValue}
              value={row.value}
              disabled={disabled}
              onChange={(next) => setRow(key, { value: next })}
            />
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        className="w-full"
        onClick={addRow}
      >
        {addLabel}
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}
